import { createClient, type SupabaseClient } from '@supabase/supabase-js'
import { SUPABASE_PUBLISHABLE_KEY, SUPABASE_URL } from './supabaseConfig'

/**
 * One shared browser client.
 *
 * Only the publishable (anon) key is ever used here — never a service-role
 * key. All access is therefore subject to the project's RLS policies.
 */

const url = SUPABASE_URL
const key = SUPABASE_PUBLISHABLE_KEY

/** False only if the configuration is genuinely absent. */
export const supabaseConfigured = Boolean(url && key)

export const supabase: SupabaseClient | null = supabaseConfigured
  ? createClient(url, key, {
      auth: {
        persistSession: true,
        autoRefreshToken: true,
        detectSessionInUrl: false,
      },
    })
  : null

if (!supabaseConfigured) {
  console.error(
    '[supabase] client not created — URL or publishable key missing. Check lib/supabaseConfig.ts.',
  )
}

/**
 * Clients never see a Supabase login. They enter a display name; this quietly
 * establishes the anonymous session that carries their inserts.
 */
export async function ensureAnonSession(): Promise<string | null> {
  if (!supabase) return null
  try {
    const { data } = await supabase.auth.getSession()
    if (data.session?.user?.id) return data.session.user.id

    const { data: signedIn, error } = await supabase.auth.signInAnonymously()
    if (error) {
      console.warn('[supabase] anonymous sign-in failed:', error.message)
      return null
    }
    return signedIn.user?.id ?? null
  } catch (error) {
    console.warn('[supabase] auth unavailable:', error)
    return null
  }
}
